// Import dependencies
import SockJS from 'sockjs-client';
import { Client } from '@stomp/stompjs';

/**
 * Service WebSocket utilisant STOMP pour communiquer avec Spring Boot
 * Remplace Socket.IO pour utiliser WebSocket natif de Spring Boot
 */
class WebSocketService {
  constructor() {
    this.stompClient = null;
    this.sockJS = null;
    this.isConnected = false;
    this.listeners = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
  }

  connect(token) {
    if (this.isConnected === true && this.stompClient?.connected === true) {
      console.log('WebSocket already connected');
      return this.stompClient;
    }

    this.lastToken = token;

    console.log('Connecting to WebSocket server...');

    const connectHeaders = {};
    if (token) {
      const cleanToken = token.startsWith('Bearer ') ? token.split(' ')[1] : token;
      connectHeaders.Authorization = `Bearer ${cleanToken}`;
    }

    // Get API base URL from environment config
    // WebSocket uses the same base URL as the API (Spring Boot backend)
    const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
    // Remove /api suffix and convert to WebSocket URL
    // SockJS uses http/https, not ws/wss
    const baseUrl = apiBaseUrl.replace('/api', '');
    const wsUrl = `${baseUrl}/ws`;

    // Create SockJS connection
    this.sockJS = new SockJS(wsUrl);

    // Create STOMP client
    this.stompClient = new Client({
      webSocketFactory: () => this.sockJS,
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
      connectHeaders,
      debug: (str) => {
        // Only log errors in production
        if (str.includes('ERROR') || str.includes('error')) {
          console.error('STOMP:', str);
        }
      },
      onConnect: (frame) => {
        console.log('✅ WebSocket connected:', frame);
        this.isConnected = true;
        this.reconnectAttempts = 0;

        // Join agent room (equivalent to Socket.IO join_agent_room)
        this.stompClient.publish({
          destination: '/app/agent/join',
          body: JSON.stringify({})
        });

        // Subscribe to call events
        this.stompClient.subscribe('/user/queue/agent/incoming_call_received', (message) => {
          const data = JSON.parse(message.body);
          console.log('Received incoming_call_received event:', data);
          this.notifyListeners('incoming_call_received', data);
        });

        this.stompClient.subscribe('/user/queue/agent/call_outbound', (message) => {
          const data = JSON.parse(message.body);
          console.log('Received call_outbound event:', data);
          this.notifyListeners('call_outbound', data);
        });

        this.stompClient.subscribe('/user/queue/agent/call_status_changed', (message) => {
          const data = JSON.parse(message.body);
          console.log('Received call_status_changed event:', data);
          this.notifyListeners('call_status_changed', data);
        });

        // Notify all listeners of connection
        this.notifyListeners('connect', frame);
      },
      onDisconnect: () => {
        console.log('❌ WebSocket disconnected');
        this.isConnected = false;
        this.notifyListeners('disconnect');
      },
      onStompError: (frame) => {
        console.error('❌ STOMP error:', frame);
        this.isConnected = false;
        this.notifyListeners('error', frame);

        // Try to reconnect if not exceeded max attempts
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectAttempts++;
          setTimeout(() => {
            console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);
            this.connect(this.lastToken);
          }, 5000);
        } else {
          console.error('Max reconnection attempts reached');
        }
      },
      onWebSocketError: (event) => {
        console.error('❌ WebSocket error:', event);
        this.isConnected = false;
        this.notifyListeners('error', event);
      }
    });

    // Add authentication headers
    this.stompClient.beforeConnect = () => {
      // Add token to headers if needed
      // Note: Spring Boot WebSocket authentication is typically handled via HTTP handshake
    };

    // Activate the STOMP client
    this.stompClient.activate();

    return this.stompClient;
  }

  disconnect() {
    if (this.stompClient) {
      this.stompClient.deactivate();
      this.stompClient = null;
    }
    if (this.sockJS) {
      this.sockJS.close();
      this.sockJS = null;
    }
    this.isConnected = false;
    this.listeners.clear();
    console.log('WebSocket disconnected manually');
  }

  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);

    // If already connected, call callback immediately for 'connect' event
    if (event === 'connect' && this.isConnected) {
      callback();
    }
  }

  off(event, callback) {
    if (!this.listeners.has(event)) return;

    const callbacks = this.listeners.get(event);
    if (!callback) {
      this.listeners.set(event, []);
      return;
    }
    const index = callbacks.indexOf(callback);
    if (index > -1) {
      callbacks.splice(index, 1);
    }
  }

  emit(destination, data) {
    if (!this.stompClient || !this.isConnected) {
      console.warn('WebSocket not connected. Cannot send message to:', destination);
      return;
    }

    this.stompClient.publish({
      destination: `/app${destination}`,
      body: JSON.stringify(data)
    });
  }

  subscribe(destination, callback) {
    if (!this.stompClient || !this.isConnected) {
      console.warn('WebSocket not connected. Cannot subscribe to:', destination);
      return null;
    }

    return this.stompClient.subscribe(destination, (message) => {
      try {
        const data = JSON.parse(message.body);
        callback(data);
      } catch (e) {
        // If not JSON, pass raw body
        callback(message.body);
      }
    });
  }

  notifyListeners(event, data) {
    const callbacks = this.listeners.get(event) || [];
    callbacks.forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        console.error('Error in WebSocket listener:', error);
      }
    });
  }

  isConnected() {
    try {
      return this.isConnected === true && this.stompClient?.connected === true;
    } catch (error) {
      console.error('Error checking connection status:', error);
      return false;
    }
  }
  
  // Getter pour éviter les conflits de nommage
  getConnectionStatus() {
    try {
      return this.isConnected === true && this.stompClient?.connected === true;
    } catch (error) {
      return false;
    }
  }

  getClient() {
    return this.stompClient;
  }
}

// Singleton instance
const webSocketService = new WebSocketService();

export default webSocketService;

